import React, { useEffect, useRef, useState } from "react";
import type { ComponentProps } from "./index";
import { useWorkspaceJson } from "./useWorkspaceJson";
import { asStringArray, filterByIds, isUrlValue, normalizeRows, pickColumns, preticked, resolveAnsweredRows, rowId, type Row } from "./tableData";
import { GONE_NOTICE, isGone, isGoneStatus, parseInteractiveResult, selectedIdsFromOutcome, submitInteractiveResult } from "./interactive";

type SubmitState =
	| { phase: "idle" }
	| { phase: "submitting" }
	| { phase: "submitted" }
	| { phase: "failed"; status: number; message: string };

/**
 * Renders `{ source, ids?, columns?, details?, prompt? }` as the same table `show_jobs` draws,
 * with a checkbox column in front and a submit button under it; result is `{ selected: string[] }`.
 *
 * It is a table on purpose. The rows a user picks from are the rows they were just reading, so
 * a pick component that drew them differently — a list, every field flattened into one line —
 * made them re-read the same postings in a second shape. Same columns, same links, same
 * disclosure row for `details`; the checkbox is a cell of its own, so a link in the row stays a
 * link and does not toggle the box it would otherwise share a label with.
 *
 * `source`/`ids`/`columns` are references, fetched and filtered exactly like Table.tsx (same
 * reasoning as there: the model says which rows and columns, this component fetches what they
 * actually contain, so a match score or an apply URL cannot drift on its way to the screen). The
 * only thing this adds on top of Table is the interaction: a checkbox per row and a submit button
 * that posts the checked ids back to the suspended tool call at
 * `POST /api/sessions/:id/components/:callId`.
 *
 * `result`'s presence is the only "already answered" signal (see `ComponentProps` in index.tsx) —
 * after a page refresh this renders read-only purely because the transcript already has a
 * toolResult for this call. The `submit` state below (submitting/submitted/failed) only bridges
 * the gap between a successful POST and that toolResult actually landing back through the SSE
 * stream (Chat.tsx); it never overrides what `result` says, and it does not survive a remount —
 * there is no second "was this submitted" flag stored anywhere.
 *
 * `answered` (derived from `result`) is checked *before* every fetch/shape gate below it and must
 * stay that way: an already-answered call has to keep showing what was submitted even when
 * `source` is missing, still loading, failed to fetch, no longer parses as a table, or its rows no
 * longer include some (or all) of the submitted ids — none of that may ever look like "nothing was
 * selectable" or silently drop a submitted id. See `resolveAnsweredRows` (tableData.ts) for the
 * id-cross-referencing this guarantees.
 */
export function Checklist({ sessionId, callId, args, result }: ComponentProps): React.ReactNode {
	const source = typeof args.source === "string" ? args.source : undefined;
	const ids = asStringArray(args.ids);
	const columns = asStringArray(args.columns);
	const detailsField = typeof args.details === "string" ? args.details : undefined;
	const prompt = typeof args.prompt === "string" ? args.prompt : undefined;

	const { data, error, loading } = useWorkspaceJson<unknown>(sessionId, source);
	const [checked, setChecked] = useState<Set<string>>(new Set());
	const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
	const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });
	const touched = useRef(false);

	// The rows arrive after the first render, so a list that starts ticked is ticked once they do --
	// and never over a choice the reader already made.
	useEffect(() => {
		const loaded = normalizeRows(data);
		if (touched.current || result || !loaded) return;
		setChecked(new Set(preticked(args.checked, filterByIds(loaded, ids))));
	}, [data]);

	const answered = result ? parseInteractiveResult(result) : undefined;
	const answeredIds = selectedIdsFromOutcome(answered);
	const unreadable = answered?.kind === "unparseable" || (answered?.kind === "value" && answeredIds === undefined);
	// The call is gone: cancelled, timed out, or the server restarted. Not an error and not the
	// reader's doing — the rows and their ticks stay on screen, disabled. See `isGone`.
	const gone = isGone(answered, submit.phase === "failed" ? submit.status : undefined);
	const locked =
		answered !== undefined ||
		submit.phase === "submitting" ||
		submit.phase === "submitted" ||
		(submit.phase === "failed" && isGoneStatus(submit.status));

	function toggleChecked(id: string) {
		touched.current = true;
		setChecked((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function toggleExpanded(id: string) {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	async function onSubmit() {
		setSubmit({ phase: "submitting" });
		const outcome = await submitInteractiveResult(sessionId, callId, { selected: [...checked] });
		if (outcome.ok) {
			setSubmit({ phase: "submitted" });
			return;
		}
		setSubmit({ phase: "failed", status: outcome.status, message: outcome.message });
	}

	// A result that cannot be read at all (malformed, or cancelled/timed out) has nothing to cross
	// -reference against `source` — say so plainly and stop. The bug this guards against is letting
	// a *readable* answer get hidden behind a *source* problem below.
	if (unreadable) {
		return (
			<div className="component checklist-component">
				{prompt && <p className="component-prompt">{prompt}</p>}
				<p className="error">已提交，无法读取所选内容</p>
			</div>
		);
	}
	// A cancelled call does not return early: the rows are still true, only the submit is gone,
	// so it falls through to the ordinary render, which `locked` disables, and picks up the quiet
	// notice at the bottom rather than replacing the reader's selection with an error.

	// From here, `answered` is either undefined (not yet answered) or a real `string[]` of
	// submitted ids (possibly empty — a legitimate "selected nothing"). Only the *not yet
	// answered* path may treat a fetch/shape problem as a blocking error — there is genuinely
	// nothing to check boxes against without real rows. An answered call falls through every one
	// of these instead of returning, and renders its own view further down.
	if (answered === undefined && !source) {
		return <div className="component error">checklist 组件缺少 source 参数，收到的参数：{JSON.stringify(args)}</div>;
	}
	if (answered === undefined && loading) return <ChecklistSkeleton />;
	if (answered === undefined && error) return <div className="component error">{error}</div>;

	const rows = source && !loading && !error ? normalizeRows(data) : null;
	if (answered === undefined && rows === null) {
		return (
			<div className="component error">
				{source} 不是表格数据：既非数组，也无 items 字段
			</div>
		);
	}

	const filtered = rows ? filterByIds(rows, ids) : [];
	if (answered === undefined && filtered.length === 0) {
		return (
			<div className="component">
				<p className="hint">无可选行</p>
			</div>
		);
	}
	const cols = pickColumns(filtered, columns).filter((c) => c !== detailsField);

	// Cancelled, and the rows behind it are no longer readable either (still loading, the fetch
	// failed, or the source moved on). The not-yet-answered case returned above, so an empty
	// table here would render as a bare header with nothing under it — say the one useful thing
	// instead.
	if (gone && filtered.length === 0) {
		return (
			<div className="component checklist-component">
				{prompt && <p className="component-prompt">{prompt}</p>}
				<p className="hint">{GONE_NOTICE.checklist}</p>
			</div>
		);
	}

	// A recorded answer, not merely an answered call: a cancelled one has no `selected` to show
	// and belongs in the live-shaped render below, with whatever the reader had ticked.
	if (answeredIds !== undefined) {
		const resolved = resolveAnsweredRows(answeredIds, filtered);
		const unresolvedCount = resolved.filter((r) => r.row === undefined).length;
		const note = sourceNote(source, loading, error, rows, filtered);
		return (
			<div className="component checklist-component">
				{prompt && <p className="component-prompt">{prompt}</p>}
				{resolved.length === 0 ? (
					<p className="hint">未选中任何行</p>
				) : (
					<div className="table-wrap">
						<table className="data-table pick-table">
							<thead>
								<tr>
									<th className="pick-cell" />
									{cols.map((c) => (
										<th key={c}>{c}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{resolved.map(({ id, row }) => (
									<tr key={id}>
										<td className="pick-cell">
											<input type="checkbox" checked readOnly disabled />
										</td>
										{row ? (
											cols.map((c, n) => (
												<td key={c} data-label={c} className={n === 0 ? "cell-primary" : undefined} title={cellText(row[c])}>
													{renderCell(row[c])}
												</td>
											))
										) : (
											<td colSpan={cols.length} className="checklist-cell-unresolved">
												{id}（已不存在）
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{/* Only worth a separate count when there was a real (non-empty) row list to partially
				    match against — with none at all, `note` below already explains that without
				    implying a permanent loss during what might only be a transient loading state. */}
				{unresolvedCount > 0 && filtered.length > 0 && (
					<p className="hint">{`其中 ${unresolvedCount} 项已不存在`}</p>
				)}
				{note && <p className="hint">{note}</p>}
			</div>
		);
	}

	return (
		<div className="component checklist-component">
			{prompt && <p className="component-prompt">{prompt}</p>}
			<div className="table-wrap">
				<table className="data-table pick-table">
					<thead>
						<tr>
							<th className="pick-cell" />
							{cols.map((c) => (
								<th key={c}>{c}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{filtered.map((row: Row, i: number) => {
							const id = rowId(row, i);
							const detail = detailsField ? cellText(row[detailsField]) : "";
							const isOpen = expanded.has(id);
							return (
								<React.Fragment key={id}>
									<tr className={checked.has(id) ? "picked" : undefined}>
										<td className="pick-cell">
											<input
												type="checkbox"
												aria-label={`选中 ${id}`}
												checked={checked.has(id)}
												disabled={locked}
												onChange={() => toggleChecked(id)}
											/>
										</td>
										{cols.map((c, n) => (
											<td key={c} data-label={c} className={n === 0 ? "cell-primary" : undefined} title={cellText(row[c])}>
												{n === 0 && detail ? (
													<button type="button" className="row-toggle" aria-expanded={isOpen} onClick={() => toggleExpanded(id)}>
														{isOpen ? "▾" : "▸"}
													</button>
												) : null}
												{renderCell(row[c])}
											</td>
										))}
									</tr>
									{isOpen && detail ? (
										<tr className="row-detail">
											<td colSpan={cols.length + 1}>{detail}</td>
										</tr>
									) : null}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>

			{gone ? <p className="hint">{GONE_NOTICE.checklist}</p> : submit.phase === "failed" ? <p className="error">{submit.message}</p> : null}

			{/* No submit row once the call is gone: a disabled button under a disabled table is
			    two controls saying the same thing, and the notice above already says it in words.
			    Every other state keeps it, including a retryable failure. */}
			{!gone && (
				<div className="checklist-actions">
					<span className="hint">{checked.size > 0 && `已选 ${checked.size} 项`}</span>
					<button onClick={() => void onSubmit()} disabled={locked}>
						{submit.phase === "submitting" ? "提交中…" : submit.phase === "submitted" ? "已提交" : "提交"}
					</button>
				</div>
			)}
		</div>
	);
}

/**
 * Explains, in the answered view, why `source` couldn't be fully cross-referenced — `undefined`
 * when there is nothing to explain (the common case: it resolved fine and every submitted id was
 * found). Never a blocking error in this view; always just an honest caveat alongside the answer.
 */
function sourceNote(source: string | undefined, loading: boolean, error: string | null, rows: Row[] | null, filtered: Row[]): string | undefined {
	if (!source) return "缺少 source 参数，无法核对所选行";
	if (loading) return "选项加载中，暂无法核对所选行";
	if (error) return `无法核对所选行：${error}`;
	if (rows === null) return `${source} 不是表格数据，无法核对所选行`;
	if (filtered.length === 0) return "原可选行已不存在，无法核对";
	return undefined;
}

function cellText(value: unknown): string {
	if (value === null || value === undefined) return "";
	return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/** Same cell rendering as Table.tsx: a URL is a link. Safe here because the checkbox has its own cell, not a shared label. */
function renderCell(value: unknown): React.ReactNode {
	if (value === null || value === undefined) return "";
	if (isUrlValue(value)) {
		return (
			<a href={value} target="_blank" rel="noreferrer noopener">
				{value}
			</a>
		);
	}
	return cellText(value);
}

function ChecklistSkeleton() {
	return (
		<div className="component table-skeleton" aria-hidden="true">
			<div className="skeleton-row" />
			<div className="skeleton-row" />
			<div className="skeleton-row" />
		</div>
	);
}
